<script>
import BaseSummaryCard from '@modules/crm/components/base-summay-card/base-summary-card.vue'
import { formatNumber } from '@utils/Format'
import {
  SKELETON_KPI_KEYS,
  TIPOS_COM_TAXA,
  TIPOS_COM_PRECO_MEDIO,
  TIPO_SALDO_EM_CONTA,
  assetTipo,
} from '@modules/consolidator/constants/AssetDetailsFicha'
import { FRACTION_TO_PERCENT } from '@modules/consolidator/constants/AssetMovements'
import { formatAssetTaxa } from '@modules/consolidator/utils/format-asset-taxa/format-asset-taxa'
import { resolveAssetSaldo } from '@modules/consolidator/utils/resolve-asset-saldo/resolve-asset-saldo'
import {
  formatQuantityDisplay,
  hasQuantityValue,
} from '@modules/consolidator/utils/format-quantity/format-quantity'

/**
 * Meses até uma data DD/MM/YYYY, por componentes de string — sem Date/moment
 * de propósito (datas civis não podem sofrer deslocamento de timezone).
 */
const monthsUntil = (dateStr) => {
  const parts = (dateStr || '').split('/')
  if (parts.length !== 3) {
    return null
  }
  const day = Number(parts[0])
  const month = Number(parts[1])
  const year = Number(parts[2])
  if (!day || !month || !year) {
    return null
  }
  const now = new Date()
  let meses = (year - now.getFullYear()) * 12 + (month - (now.getMonth() + 1))
  if (day < now.getDate()) {
    meses -= 1
  }
  return meses
}
const PERCENT_DECIMALS = 1

export default {
  name: 'AssetKpis',
  components: { BaseSummaryCard },
  props: {
    asset: {
      type: Object,
      required: true,
    },
    /** série do ativo no PERÍODO ATUAL do gráfico — fonte da rentabilidade acumulada */
    serie: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    tipo() {
      return this.asset['Tipo'] || this.asset.tipo || ''
    },
    saldoAtual() {
      return resolveAssetSaldo({ asset: this.asset }) || null
    },
    saldoSub() {
      const quantidade = this.asset.quantidade
      const usaPrecoMedio = TIPOS_COM_PRECO_MEDIO.includes(assetTipo(this.asset))
      const preco = usaPrecoMedio ? this.asset.preco_medio : this.asset.pu
      const rotuloPreco = usaPrecoMedio ? 'PM' : 'PU'
      if (!quantidade && !preco) {
        return null
      }
      const parts = []
      if (hasQuantityValue(quantidade)) {
        parts.push(`${formatQuantityDisplay(quantidade)} un`)
      }
      if (preco && preco !== '-') {
        parts.push(`${rotuloPreco} ${preco}`)
      }
      return parts.join(' · ')
    },
    rentabilidadeAcumulada() {
      const serie =
        (this.serie && this.serie.rentabilidade_acumulada) || []
      if (serie.length < 2) {
        return null
      }
      return serie[serie.length - 1] * FRACTION_TO_PERCENT
    },
    rentabilidadeSub() {
      const cdi =
        (this.serie &&
          this.serie.benchmark &&
          this.serie.benchmark.CDI) ||
        []
      if (!cdi.length) {
        return null
      }
      const acumulado = cdi[cdi.length - 1] * FRACTION_TO_PERCENT
      return `CDI no período: ${this.signal(acumulado)}${formatNumber(
        Math.abs(acumulado),
        PERCENT_DECIMALS
      )}%`
    },
    taxa() {
      if (!TIPOS_COM_TAXA.includes(this.tipo)) {
        return null
      }
      return formatAssetTaxa(this.asset['Taxa'] || this.asset.taxa) || null
    },
    vencimento() {
      if (
        !TIPOS_COM_TAXA.includes(this.tipo) ||
        this.tipo === TIPO_SALDO_EM_CONTA
      ) {
        return null
      }
      const formatted = this.asset['data_vencimento_f']
      return formatted && formatted !== '-' ? formatted : null
    },
    vencimentoSub() {
      const meses = monthsUntil(this.vencimento)
      if (meses === null) {
        return null
      }
      if (meses < 0) {
        return 'vencido'
      }
      return `em ${meses} meses`
    },
    kpis() {
      const items = [
        {
          key: 'saldo',
          label: 'Saldo atual',
          value: this.saldoAtual,
          sub: this.saldoSub,
        },
        {
          key: 'rentabilidade',
          label: 'Rentabilidade acumulada',
          value:
            this.rentabilidadeAcumulada === null
              ? null
              : `${this.signal(this.rentabilidadeAcumulada)}${formatNumber(
                  Math.abs(this.rentabilidadeAcumulada),
                  PERCENT_DECIMALS
                )}%`,
          sub: this.rentabilidadeSub,
          positive:
            this.rentabilidadeAcumulada !== null &&
            this.rentabilidadeAcumulada >= 0,
          negative:
            this.rentabilidadeAcumulada !== null &&
            this.rentabilidadeAcumulada < 0,
        },
        { key: 'taxa', label: 'Taxa', value: this.taxa, sub: null },
        {
          key: 'vencimento',
          label: 'Vencimento',
          value: this.vencimento,
          sub: this.vencimentoSub,
        },
      ]
      return items.filter((item) => Boolean(item.value))
    },
    items() {
      return this.kpis.map((kpi) => ({
        key: kpi.key,
        label: kpi.label,
        value: kpi.value,
        description: kpi.sub || '',
        class: this.corDoValor(kpi),
      }))
    },
    esqueletoAtivo() {
      return this.loading && !this.items.length
    },
    itemsComEsqueleto() {
      if (!this.esqueletoAtivo) {
        return this.items
      }
      return SKELETON_KPI_KEYS.map((key) => ({ key, label: '', value: '' }))
    },
  },
  methods: {
    signal(value) {
      return value >= 0 ? '+' : '-'
    },
    corDoValor(kpi) {
      if (kpi.positive) {
        return 'text-success'
      }
      if (kpi.negative) {
        return 'text-danger'
      }
      return ''
    },
  },
}
</script>

<template>
  <BaseSummaryCard
    v-if="esqueletoAtivo || items.length"
    style-card="bordered"
    :items="itemsComEsqueleto"
    :loading="esqueletoAtivo"
    class="asset-kpis mb-4"
  />
</template>
